/**
 * PATH: packages/email-hub/src/feature/components/email-context-menu/AllocationActions.tsx
 * FILE_ID: EMAILHUB-CONTEXTMENU-ALLOCATION-ACTIONS-001
 * PURPOSE: Board, bill, quote-request, and AI filing actions.
 * WHY: Groups allocation-domain behavior outside the provider frame.
 */
import { FileText, LayoutGrid, Receipt, Wand2 } from 'lucide-react'
import { AllocateToKanbanSubMenu } from '@quotech/kanban-host'

import {
  ContextMenuItem,
  ContextMenuSeparator,
} from '../../../_internal/components/ContextMenu'
import { buildKanbanBoardLink } from '../../lib/kanbanDeepLink'
import { hasAiCopilotHost, useAiCopilotStore } from '../../store/aiCopilotStore'
import {
  deriveAiSuggestion,
  derivePriorAllocationId,
} from './allocationHelpers'
import type { EmailContextMenuController } from './useEmailContextMenuController'

export function AllocationActions({
  controller,
}: {
  controller: EmailContextMenuController
}) {
  const {
    applyQuoteRequest,
    canRequestRule,
    ctxMenu,
    handleKanbanAllocated,
    kanbanApi,
    markAsBill,
    navigate,
    orgId,
    quoteReqBusy,
    session,
    setCreateBoardFor,
    setCtxMenu,
    setRequestRuleFor,
    toast,
  } = controller
  if (!ctxMenu) return null

  const email = ctxMenu.email
  const emailIds = ctxMenu.emailIds.length > 0 ? ctxMenu.emailIds : [email.id]
  const bulk = emailIds.length > 1
  const boardLink = bulk ? null : buildKanbanBoardLink(email.allocation_summary)

  return (
    <>
      {boardLink && (
        <>
          <ContextMenuItem
            icon={LayoutGrid}
            onClick={() => {
              setCtxMenu(null)
              navigate(boardLink)
            }}
          >
            Go to board
          </ContextMenuItem>
          <ContextMenuSeparator />
        </>
      )}
      <ContextMenuItem
        icon={Receipt}
        disabled={markAsBill.busy}
        onClick={async () => {
          const subject = email.subject || '(no subject)'
          setCtxMenu(null)
          const result = await markAsBill.run(email.id)
          if (!result) {
            toast(
              markAsBill.error || `Could not allocate "${subject}" to Bills`,
              'error',
            )
            return
          }
          toast(
            result.created
              ? 'Allocated to Bills - opening bill…'
              : 'This email is already in Bills - opening…',
            'success',
          )
          navigate(`/bills/${result.bill_id}`)
        }}
      >
        Allocate to Bills
      </ContextMenuItem>
      <ContextMenuItem
        icon={FileText}
        disabled={quoteReqBusy}
        onClick={() => {
          setCtxMenu(null)
          void applyQuoteRequest(email.id, email.subject || '(no subject)')
        }}
      >
        Mark as Quote Request
      </ContextMenuItem>
      <AllocateToKanbanSubMenu
        api={kanbanApi}
        orgId={orgId}
        emailId={email.id}
        emailIds={emailIds}
        onAllocated={handleKanbanAllocated}
        notify={(kind, message) => toast(message, kind)}
        aiSuggestion={bulk ? null : deriveAiSuggestion(email)}
        priorAllocationId={bulk ? null : derivePriorAllocationId(email)}
        currentUserId={session?.user?.id ?? null}
        onRequestCreateBoard={
          orgId != null
            ? () => {
                setCreateBoardFor({ emailIds })
                setCtxMenu(null)
              }
            : undefined
        }
      />
      {canRequestRule && (
        <ContextMenuItem
          icon={Wand2}
          onClick={() => {
            if (hasAiCopilotHost()) {
              useAiCopilotStore
                .getState()
                .openFor(
                  email.id,
                  `File emails like this${email.from_address ? ` (from ${email.from_address})` : ''} to `,
                )
            } else {
              setRequestRuleFor({
                emailId: email.id,
                senderAddress: email.from_address ?? null,
                category: email.category ?? null,
              })
            }
            setCtxMenu(null)
          }}
        >
          Ask AI — file emails like this…
        </ContextMenuItem>
      )}
      <ContextMenuSeparator />
    </>
  )
}
